import { TrashIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import type { FormEvent, ChangeEvent } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { apiUrl, authFetch, authService } from '../utils/api';

interface Session {
  id: number;
  name: string;
  description: string;
  startTime: string;
  createdAt: string;
  courseId: number | null;
  courseName: string | null;
  unitId: number | null;
  unitName: string | null;
  isActive: boolean;
  attendanceCount: number;
  grade: string;
  term: string;
  location: string;
}

interface FormData {
  name: string;
  description: string;
  startTime: string;
  grade: string;
  term: string;
  courseId: string;
  unitId: string;
  location: string;
}

interface SortConfig {
  key: keyof Session;
  direction: 'asc' | 'desc';
}

const Sessions = () => {
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [filters, setFilters] = useState({
    sessionName: '',
    grade: '',
    term: '',
    courseId: '',
  });
  const [displayedSessions, setDisplayedSessions] = useState<Session[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    startTime: '',
    grade: '',
    term: '',
    courseId: '',
    unitId: '',
    location: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
  const [sessionToToggle, setSessionToToggle] = useState<Session | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [courses, setCourses] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  
  // Fetch sessions from API on mount
  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl(`Session/all?pageNumber=${currentPage}&pageSize=${itemsPerPage}${filters.sessionName ? `&sessionName=${encodeURIComponent(filters.sessionName)}` : ''}${filters.grade ? `&grade=${encodeURIComponent(filters.grade)}` : ''}${filters.term ? `&term=${encodeURIComponent(filters.term)}` : ''}${filters.courseId ? `&courseId=${encodeURIComponent(filters.courseId)}` : ''}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch sessions');
      const data = await response.json();
      
      if (data.success) {
        // Handle the correct API response structure
        const responseData = data.data;
        const sessionsData = Array.isArray(responseData.data) ? responseData.data : [];
        setSessions(sessionsData);
        setDisplayedSessions(sessionsData);
        setTotalItems(responseData.totalCount || 0);
        setTotalPages(responseData.totalPages || 1);
      } else {
        throw new Error(data.message || 'Failed to fetch sessions');
      }
    } catch (error) {
      setSessions([]);
      setDisplayedSessions([]);
      setError('Failed to fetch sessions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [currentPage, filters.sessionName, filters.grade, filters.term, filters.courseId]);

  // Fetch courses based on grade and term
  const fetchCourses = async (grade: string, term: string) => {
    if (!grade || !term) {
      setCourses([]);
      return;
    }
    
    setLoadingCourses(true);
    try {
      const response = await authFetch(
        apiUrl(`Course/bygradeandterm?grade=${grade}&term=${term}&pageNumber=1&pageSize=100`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCourses(data.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Fetch units based on course ID
  const fetchUnits = async (courseId: string) => {
    if (!courseId) {
      setUnits([]);
      return;
    }
    
    setLoadingUnits(true);
    try {
      const response = await authFetch(
        apiUrl(`Course/tree?courseid=${courseId}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUnits(data.data.units || []);
        }
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  // Sort function should only sort the current page's data
  const handleSort = (key: keyof Session) => {
    setSortConfig((prevSortConfig) => {
      const direction = prevSortConfig?.key === key && prevSortConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  // Apply sorting to the current page of data
  const sortedSessions = useMemo(() => {
    // Ensure displayedSessions is always an array
    if (!Array.isArray(displayedSessions)) {
      return [];
    }
    
    if (!sortConfig) return displayedSessions;
    
    return [...displayedSessions].sort((a, b) => {
      const key = sortConfig.key;
      const direction = sortConfig.direction;
      
      // Use type assertion to avoid TypeScript errors
      const aValue = a[key] as string | number;
      const bValue = b[key] as string | number;
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayedSessions, sortConfig]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      sessionName: '',
      grade: '',
      term: '',
      courseId: '',
    });
    setCurrentPage(1); // Reset to first page
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value
      };

      // Reset dependent fields when grade or term changes
      if (name === 'grade' || name === 'term') {
        newData.courseId = '';
        newData.unitId = '';
        setUnits([]);
        // Fetch courses when both grade and term are selected
        if (name === 'grade' && newData.term) {
          fetchCourses(value, newData.term);
        } else if (name === 'term' && newData.grade) {
          fetchCourses(newData.grade, value);
        }
      }

      // Reset unit when course changes
      if (name === 'courseId') {
        newData.unitId = '';
        setUnits([]);
        // Fetch units when course is selected
        if (value) {
          fetchUnits(value);
        }
      }

      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      const response = await authFetch(apiUrl('Session/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          startTime: formData.startTime,
          grade: formData.grade,
          term: formData.term,
          courseId: formData.courseId ? parseInt(formData.courseId) : null,
          unitId: formData.unitId ? parseInt(formData.unitId) : null,
          location: formData.location,
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh the session list after successful addition
        setCurrentPage(1); // Go back to first page
        setShowAddForm(false);
        setFormData({
          name: '',
          description: '',
          startTime: '',
          grade: '',
          term: '',
          courseId: '',
          unitId: '',
          location: '',
        });
        fetchSessions();
        alert('تم إضافة الحصة بنجاح!');
      } else {
        alert('فشل في إضافة الحصة: ' + data.message);
      }
    } catch (error) {
      console.error('Error adding session:', error);
      alert('فشل في إضافة الحصة. يرجى المحاولة مرة أخرى.');
    }
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(true);
    
    try {
      const response = await authFetch(apiUrl(`Session/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If API call succeeds, update the local state
        setSessions(sessions.filter((session: Session) => session.id !== id));
        setDisplayedSessions(displayedSessions.filter((session: Session) => session.id !== id));
        setSessionToDelete(null);
      } else {
        alert('Failed to delete session: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Failed to delete session. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (session: Session) => {
    try {
      const newStatus = !session.isActive;
      
      const response = await authFetch(apiUrl(`Session/${session.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          id: session.id,
          name: session.name,
          description: session.description,
          startTime: session.startTime,
          grade: session.grade,
          term: session.term,
          courseId: session.courseId,
          location: session.location,
          isActive: newStatus,
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If successful, update the local state and close the modal
        setSessions(
          sessions.map((s: Session) =>
            s.id === session.id
              ? {
                  ...s,
                  isActive: newStatus,
                }
              : s
          )
        );
        setDisplayedSessions(
          displayedSessions.map((s: Session) =>
            s.id === session.id
              ? {
                  ...s,
                  isActive: newStatus,
                }
              : s
          )
        );
        setSessionToToggle(null);
      } else {
        alert('Failed to update session status: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating session status:', error);
      alert('Failed to update session status. Please try again later.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Add 2 hours to the time
    date.setHours(date.getHours() + 2);
    
    // Format date as "3 اكتوبر 2025"
    const day = date.getDate();
    const monthNames = [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    // Format time as "07:10 م" (12-hour format with Arabic AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'م' : 'ص';
    const displayHours = hours % 12 || 12;
    
    return `${day} ${month} ${year} في ${displayHours}:${minutes} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                حصص السنتر
              </h1>
              <p className="mt-2 text-gray-600">إدارة حصص السنتر</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                         shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              إضافة حصة
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <label htmlFor="session-name-filter" className="block text-sm font-medium text-gray-700">
                اسم الحصة
              </label>
              <input
                type="text"
                id="session-name-filter"
                value={filters.sessionName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, sessionName: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="البحث في الحصص..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700">
                المستوى الدراسي
              </label>
              <select
                id="grade-filter"
                value={filters.grade}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, grade: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع المستويات</option>
                <option value="Secondary1">الصف الأول الثانوي</option>
                <option value="Secondary2">الصف الثاني الثانوي</option>
                <option value="Secondary3">الصف الثالث الثانوي</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="term-filter" className="block text-sm font-medium text-gray-700">
                الفصل الدراسي
              </label>
              <select
                id="term-filter"
                value={filters.term}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, term: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع الفصول</option>
                <option value="First">الفصل الأول</option>
                <option value="Second">الفصل الثاني</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="course-filter" className="block text-sm font-medium text-gray-700">
                الدورة
              </label>
              <input
                type="text"
                id="course-filter"
                value={filters.courseId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, courseId: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="معرف الدورة..."
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    اسم الحصة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الوصف
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    وقت البداية
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الفصل
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الموقع
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    عدد الحضور
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : !loading && totalItems === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على حصص</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : Array.isArray(sortedSessions) && sortedSessions.length > 0 ? (
                  sortedSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => navigate(`/sessions/${session.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{session.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate">{session.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{formatDate(session.startTime)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{session.term}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{session.location}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-medium">{session.attendanceCount}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                          ${session.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'}`}
                        >
                          {session.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-reverse space-x-2">
                          <button
                            onClick={() => setSessionToToggle(session)}
                            className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                              session.isActive
                                ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={session.isActive ? 'إخفاء الحصة' : 'إظهار الحصة'}
                          >
                            {session.isActive ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => setSessionToDelete(session)}
                            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                     transition-colors duration-200"
                            title="حذف الحصة"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على حصص</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && totalItems > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white rounded-2xl shadow-lg px-6 py-4">
            <div className="text-sm text-gray-700 mb-4 sm:mb-0">
              عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>{' '}
              من <span className="font-medium">{totalItems}</span> نتيجة
            </div>
            <div className="flex items-center space-x-reverse space-x-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأول
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  if (index > 0 && prevPage && page - prevPage > 1) {
                    return [
                      <span key={`ellipsis-${prevPage}`} className="px-3 py-2">...</span>,
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                          ${currentPage === page
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                          }`}
                      >
                        {page}
                      </button>
                    ];
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                        ${currentPage === page
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأخير
              </button>
            </div>
          </div>
        )}

        {/* Add Session Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة حصة جديدة</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      اسم الحصة
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      name="name"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                      placeholder="أدخل اسم الحصة"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      وصف الحصة
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={handleInputChange}
                      name="description"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      rows={3}
                      required
                      placeholder="أدخل وصف الحصة"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      وقت البداية
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      name="startTime"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      المستوى
                    </label>
                    <select
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    >
                      <option value="">اختر المستوى</option>
                      <option value="Secondary1">الصف الأول الثانوي</option>
                      <option value="Secondary2">الصف الثاني الثانوي</option>
                      <option value="Secondary3">الصف الثالث الثانوي</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الفصل الدراسي
                    </label>
                    <select
                      value={formData.term}
                      onChange={handleInputChange}
                      name="term"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    >
                      <option value="">اختر الفصل</option>
                      <option value="First">الفصل الأول</option>
                      <option value="Second">الفصل الثاني</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الدورة
                    </label>
                    <select
                      value={formData.courseId}
                      onChange={handleInputChange}
                      name="courseId"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      disabled={!formData.grade || !formData.term || loadingCourses}
                    >
                      <option value="">
                        {loadingCourses 
                          ? 'جاري تحميل الدورات...' 
                          : !formData.grade || !formData.term
                          ? 'اختر المستوى والفصل أولاً'
                          : 'اختر الدورة (اختياري)'
                        }
                      </option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.courseName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الوحدة
                    </label>
                    <select
                      value={formData.unitId}
                      onChange={handleInputChange}
                      name="unitId"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      disabled={!formData.courseId || loadingUnits}
                    >
                      <option value="">
                        {loadingUnits 
                          ? 'جاري تحميل الوحدات...' 
                          : !formData.courseId
                          ? 'اختر الدورة أولاً'
                          : 'اختر الوحدة (اختياري)'
                        }
                      </option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.unitName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الموقع
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={handleInputChange}
                      name="location"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                      placeholder="أدخل موقع الحصة"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    إضافة الحصة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {sessionToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">حذف الحصة</h3>
                <p className="text-gray-600">
                  هل أنت متأكد من حذف <span className="font-bold text-gray-900">{sessionToDelete.name}</span>؟
                  لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setSessionToDelete(null)}
                  disabled={isDeleting}
                >
                  إلغاء
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center gap-2"
                  onClick={() => handleDelete(sessionToDelete.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      حذف
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {sessionToToggle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {sessionToToggle.isActive ? 'إخفاء الحصة' : 'إظهار الحصة'}
                </h3>
                <p className="text-gray-600">
                  هل أنت متأكد من أنك تريد {sessionToToggle.isActive ? 'إخفاء' : 'إظهار'} الحصة <span className="font-bold text-gray-900">{sessionToToggle.name}</span>؟
                  {sessionToToggle.isActive ? ' هذا سيجعلها غير مرئية للطلاب.' : ' هذا سيجعلها مرئية للطلاب.'}
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setSessionToToggle(null)}
                >
                  إلغاء
                </button>
                <button
                  className={`px-4 py-2 rounded-lg ${
                    sessionToToggle.isActive
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    sessionToToggle.isActive
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  }`}
                  onClick={() => handleToggleStatus(sessionToToggle)}
                >
                  {sessionToToggle.isActive ? 'إخفاء الحصة' : 'إظهار الحصة'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sessions;
      